import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import api from "../api";

/**
 * /admin/setup/:token — landing page for the invite email link.
 * 1. On mount, validates the token via GET /api/admin/setup/{token}
 * 2. If valid, lets the user set a password
 * 3. On submit, POST /api/admin/setup → redirects to /admin/login
 */
const AdminSetup = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [phase, setPhase] = useState("loading"); // loading | invalid | ready | submitting | done
    const [info, setInfo] = useState(null);
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        api.get(`/admin/setup/${encodeURIComponent(token)}`)
            .then((r) => {
                if (cancelled) return;
                if (r.data?.valid) {
                    setInfo(r.data);
                    setPhase("ready");
                } else {
                    setPhase("invalid");
                }
            })
            .catch(() => {
                if (!cancelled) setPhase("invalid");
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 10) {
            setError("La contraseña debe tener al menos 10 caracteres / Password must be at least 10 characters");
            return;
        }
        if (password !== confirm) {
            setError("Las contraseñas no coinciden / Passwords don't match");
            return;
        }
        setPhase("submitting");
        try {
            await api.post("/admin/setup", { token, password });
            setPhase("done");
        } catch (err) {
            setError(err?.response?.data?.detail || "No se pudo establecer la contraseña / Could not set password");
            setPhase("ready");
        }
    };

    return (
        <div data-testid="admin-setup-page" className="min-h-screen bg-earth-50 grain flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md">
                <div className="bg-white rounded-2xl shadow-xl border border-earth-200 overflow-hidden">
                    <div className="bg-gradient-to-br from-terracotta-500 to-terracotta-700 text-white p-7">
                        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] opacity-90 mb-1">
                            <ShieldCheck className="w-4 h-4" /> Admin Setup
                        </div>
                        <h1 className="font-serif font-black text-3xl">Activa tu cuenta</h1>
                        <p className="text-sm opacity-90 mt-1">Set up your admin account</p>
                    </div>

                    <div className="p-7">
                        {phase === "loading" && (
                            <div className="flex items-center gap-3 text-earth-600">
                                <Loader2 className="w-5 h-5 animate-spin" />
                                <span>Validando enlace… / Validating link…</span>
                            </div>
                        )}

                        {phase === "invalid" && (
                            <div data-testid="admin-setup-invalid" className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 text-red-800">
                                <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
                                <div className="text-sm">
                                    <div className="font-bold mb-1">Enlace inválido o expirado</div>
                                    <div>Pide al administrador que te envíe un nuevo enlace de invitación.</div>
                                    <div className="mt-2 opacity-70">This link is invalid or has expired. Ask your admin to send a new invite.</div>
                                </div>
                            </div>
                        )}

                        {phase === "done" && (
                            <div data-testid="admin-setup-done" className="space-y-4">
                                <div className="flex items-start gap-3 p-4 rounded-xl bg-green-50 border border-green-200 text-green-800">
                                    <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
                                    <div className="text-sm">
                                        <div className="font-bold mb-1">¡Cuenta activada! / Account activated!</div>
                                        <div>Ya puedes iniciar sesión con tu correo y la contraseña que acabas de elegir.</div>
                                    </div>
                                </div>
                                <Link
                                    to="/admin/login"
                                    data-testid="admin-setup-goto-login"
                                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold bg-terracotta-600 hover:bg-terracotta-700 text-white transition-colors"
                                >
                                    Ir al inicio de sesión / Go to login
                                </Link>
                            </div>
                        )}

                        {(phase === "ready" || phase === "submitting") && info && (
                            <form onSubmit={submit} className="space-y-4" data-testid="admin-setup-form">
                                <div className="text-sm text-earth-700">
                                    <span className="text-earth-500">Cuenta / Account: </span>
                                    <span className="font-semibold" data-testid="admin-setup-email">{info.email}</span>
                                </div>
                                <div className="text-xs text-earth-500">
                                    Restaurante / Restaurant: <span className="font-mono">{info.tenant_id}</span>
                                    {" · "}
                                    Rol / Role: <span className="font-semibold uppercase">{info.role}</span>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-earth-800 mb-1">
                                        Nueva contraseña / New password
                                    </label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        data-testid="admin-setup-password"
                                        autoComplete="new-password"
                                        required
                                        minLength={10}
                                        className="w-full px-4 py-3 rounded-xl border border-earth-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 outline-none"
                                        placeholder="Mínimo 10 caracteres"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-earth-800 mb-1">
                                        Confirma la contraseña / Confirm password
                                    </label>
                                    <input
                                        type="password"
                                        value={confirm}
                                        onChange={(e) => setConfirm(e.target.value)}
                                        data-testid="admin-setup-confirm"
                                        autoComplete="new-password"
                                        required
                                        minLength={10}
                                        className="w-full px-4 py-3 rounded-xl border border-earth-300 focus:border-terracotta-500 focus:ring-2 focus:ring-terracotta-200 outline-none"
                                    />
                                </div>

                                {error && (
                                    <div data-testid="admin-setup-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                                        {error}
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={phase === "submitting"}
                                    data-testid="admin-setup-submit"
                                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold bg-terracotta-600 hover:bg-terracotta-700 disabled:bg-earth-400 text-white transition-colors"
                                >
                                    {phase === "submitting" ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" /> Guardando…
                                        </>
                                    ) : (
                                        "Activar cuenta / Activate account"
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </div>

                <div className="text-center text-xs text-earth-500 mt-4">
                    Este enlace expira 72 horas después de ser enviado. / This link expires 72 hours after it was sent.
                </div>
            </div>
        </div>
    );
};

export default AdminSetup;
